import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  MenuItem,
  Typography
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { Edit2, Key, Plus, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import toastError from '../../errors/toastError'
import { i18nToast } from '../../helpers/i18nToast'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import ConfirmationModal from '../ConfirmationModal'
import SelectInput from '../SelectInput'
import { InputFieldText } from '../InputFieldText'
import InputMaskCustom from '../InputMaskCustom'

const emptyPixConfig = { type: 'cpf', key: '', accountName: '' }
const pixLabelMap = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
  phone: 'Telefone',
  email: 'E-mail',
  random: 'Chave Aleatória'
}

const CompanyPixSettings = ({
  settings,
  currentUser,
  update,
  classes,
  expanded,
  onExpand
}) => {
  const [companyPixConfigs, setCompanyPixConfigs] = useState([])
  const [pixSaving, setPixSaving] = useState(false)

  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [indexToDelete, setIndexToDelete] = useState(null)

  const [editingIndex, setEditingIndex] = useState(null)

  const pixMasks = {
    cpf: '999.999.999-99',
    cnpj: '99.999.999/9999-99',
    phone: '(99) 99999-9999',
    email: null,
    random: null
  }

  useEffect(() => {
    if (Array.isArray(settings) && settings.length) {
      const companyPixKeysSetting = settings.find(
        s => s.key === 'companyPixKeys'
      )
      if (companyPixKeysSetting?.value) {
        try {
          const parsed = JSON.parse(companyPixKeysSetting.value)
          setCompanyPixConfigs(parsed.length ? parsed : [])
        } catch (e) {
          setCompanyPixConfigs([])
        }
      }
      setEditingIndex(null)
    }
  }, [settings])

  const handleAddConfig = useCallback(() => {
    setCompanyPixConfigs(prev => [{ ...emptyPixConfig }, ...prev])
    setEditingIndex(0)
  }, [])

  const handleChangeConfig = useCallback((index, field, value) => {
    setCompanyPixConfigs(prev =>
      prev.map((config, idx) => {
        if (idx === index) {
          if (field === 'type') {
            return { ...config, [field]: value, key: '' }
          }
          return { ...config, [field]: value }
        }
        return config
      })
    )
  }, [])

  const handleSave = useCallback(async () => {
    if (currentUser?.profile !== 'admin') {
      toast.error('Apenas administradores podem cadastrar a chave Pix.')
      return
    }

    const configsToSave = companyPixConfigs.filter(c => c.key || c.accountName)

    if (companyPixConfigs.length > 0 && !configsToSave.length) {
      toast.error(
        'Cadastre ao menos uma chave Pix válida ou remova o formulário.'
      )
      return
    }

    if (configsToSave.some(c => !c.key || !c.accountName)) {
      toast.error('Preencha todos os campos das chaves.')
      return
    }

    setPixSaving(true)
    try {
      await update({
        key: 'companyPixKeys',
        value: JSON.stringify(configsToSave)
      })

      if (configsToSave.length > 0) {
        const first = configsToSave[0]
        await update({ key: 'companyPixKeyType', value: first.type })
        await update({ key: 'companyPixKey', value: first.key })
        await update({ key: 'companyPixAccountName', value: first.accountName })
      } else {
        // Limpar legado se todas as chaves forem removidas
        await update({ key: 'companyPixKeyType', value: '' })
        await update({ key: 'companyPixKey', value: '' })
        await update({ key: 'companyPixAccountName', value: '' })
      }

      i18nToast.success('Configurações Pix salvas com sucesso.')
      setEditingIndex(null)
    } catch (error) {
      toastError(error)
    } finally {
      setPixSaving(false)
    }
  }, [companyPixConfigs, currentUser?.profile, update])

  const handleOpenDeleteContactModal = useCallback(index => {
    setIndexToDelete(index)
    setConfirmModalOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    try {
      const nextConfigs = companyPixConfigs.filter(
        (_, idx) => idx !== indexToDelete
      )

      setCompanyPixConfigs(nextConfigs)

      await update({
        key: 'companyPixKeys',
        value: JSON.stringify(nextConfigs.filter(c => c.key && c.accountName))
      })

      i18nToast.success('Chave Pix excluída e alterações salvas!')
      setEditingIndex(null)
    } catch (error) {
      toastError(error)
    } finally {
      setIndexToDelete(null)
      setConfirmModalOpen(false)
    }
  }, [companyPixConfigs, indexToDelete, update])

  const handleCancel = useCallback(() => {
    if (Array.isArray(settings) && settings.length) {
      const companyPixKeysSetting = settings.find(
        s => s.key === 'companyPixKeys'
      )
      if (companyPixKeysSetting?.value) {
        try {
          const parsed = JSON.parse(companyPixKeysSetting.value)
          setCompanyPixConfigs(parsed.length ? parsed : [])
        } catch (e) {
          setCompanyPixConfigs([])
        }
      } else {
        setCompanyPixConfigs([])
      }
    } else {
      setCompanyPixConfigs([])
    }
    setEditingIndex(null)
  }, [settings])

  return (
    <Grid item xs={12}>
      <ConfirmationModal
        title="Excluir Chave Pix"
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false)
          setIndexToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
      >
        Deseja realmente remover esta chave Pix da lista? Essa ação não pode ser
        desfeita!
      </ConfirmationModal>

      <Grid container spacing={2} className={classes.containerPix}>
        <Grid item xs={12}>
          <ButtonPrimary
            label="Nova Chave Pix"
            endIcon={<Plus />}
            onClick={handleAddConfig}
          />
        </Grid>

        {companyPixConfigs.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" className={classes.textEmpty}>
              Nenhuma chave Pix configurada. Clique no botão acima para
              adicionar.
            </Typography>
          </Grid>
        )}

        {companyPixConfigs.map((config, index) => {
          const isSaved =
            config.key && config.accountName && editingIndex !== index
          if (isSaved) {
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`pix-card-${index}`}>
                <Box className={classes.boxPixContainer}>
                  <Box className={classes.iconPixContainer} aria-hidden="true">
                    <Key size={20} />
                  </Box>

                  <Box flexGrow={1} minWidth={0} overflow="hidden">
                    <Typography
                      variant="body1"
                      className={classes.pixAccountName}
                    >
                      {config.accountName}
                    </Typography>
                    <Box className={classes.pixType}>
                      {pixLabelMap[config.type] || config.type}
                    </Box>
                  </Box>

                  <Box className={classes.boxActions}>
                    <IconButton
                      size="small"
                      onClick={() => setEditingIndex(index)}
                      aria-label="Editar Chave Pix"
                      className={classes.editButton}
                    >
                      <Edit2 size={18} />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteContactModal(index)}
                      aria-label="Excluir Chave Pix"
                      className={classes.deleteButton}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            )
          }

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`pix-form-${index}`}>
              <Box
                component="section"
                aria-label={
                  settings
                    ?.find(s => s.key === 'companyPixKeys')
                    ?.value?.includes(config.key) && config.key
                    ? 'Editar Chave'
                    : 'Nova Chave'
                }
                className={classes.boxForm}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <SelectInput
                      label="Tipo"
                      value={config.type}
                      onChange={e =>
                        handleChangeConfig(index, 'type', e.target.value)
                      }
                      fullWidth
                    >
                      <MenuItem value="cpf">CPF</MenuItem>
                      <MenuItem value="cnpj">CNPJ</MenuItem>
                      <MenuItem value="email">E-mail</MenuItem>
                      <MenuItem value="phone">Telefone</MenuItem>
                      <MenuItem value="random">Aleatória</MenuItem>
                    </SelectInput>
                  </Grid>

                  <Grid item xs={12}>
                    {pixMasks[config.type] ? (
                      <InputMaskCustom
                        label={pixLabelMap[config.type] || 'Chave Pix'}
                        mask={pixMasks[config.type]}
                        value={config.key}
                        onChange={e =>
                          handleChangeConfig(index, 'key', e.target.value)
                        }
                        fullWidth
                      />
                    ) : (
                      <InputFieldText
                        label={
                          config.type === 'email' ? 'E-mail' : 'Chave Aleatória'
                        }
                        value={config.key}
                        onChange={e =>
                          handleChangeConfig(index, 'key', e.target.value)
                        }
                        fullWidth
                        placeholder={
                          config.type === 'email'
                            ? 'exemplo@email.com'
                            : 'Chave aleatória'
                        }
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} style={{ marginBottom: '16px' }}>
                    <InputFieldText
                      label="Nome da Conta"
                      value={config.accountName}
                      onChange={e =>
                        handleChangeConfig(index, 'accountName', e.target.value)
                      }
                      fullWidth
                      placeholder="Nome do titular da conta"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      gap="12px"
                      width="100%"
                    >
                      <ButtonSecondary
                        label="Cancelar"
                        onClick={handleCancel}
                        disabled={pixSaving}
                      />
                      <ButtonPrimary
                        label="Salvar"
                        onClick={handleSave}
                        disabled={pixSaving}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </Grid>
  )
}

export default CompanyPixSettings
